import React from 'react';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import { IconChevronUp, IconChevronDown } from '@tabler/icons-react';

/** A block action, with an optional icon and destructive treatment. */
interface BlockAction {
  label: string;
  onClick: (event: React.MouseEvent) => void;
  icon?: React.ReactNode;
  className?: string;
  disabled?: boolean;
}

/** Shared actions and compact ordering controls for authoring cards. */
interface BlockActionsMenuProps {
  trigger: React.ReactNode;
  items: BlockAction[];
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

/** Portal the menu past card boundaries and retain keyboard/focus behavior. */
export function BlockActionsMenu({ trigger, items, canMoveUp, canMoveDown, onMoveUp, onMoveDown }: BlockActionsMenuProps) {
  return (
    <Menu as="div" className="relative">
      <MenuButton as={React.Fragment}>{trigger}</MenuButton>
      <MenuItems transition anchor={{ to: 'bottom end', gap: 4 }} className="origin-top-right transition duration-200 data-closed:opacity-0 data-closed:-translate-y-1 data-closed:scale-[0.98] data-leave:duration-150 motion-reduce:transition-none z-50 w-44 rounded-lg border border-border bg-surface-raised p-1 shadow-lg focus:outline-none">
        {items.map(item => (
          <MenuItem key={item.label} disabled={item.disabled}>
            <button type="button" onClick={item.onClick} disabled={item.disabled}
              className={`flex min-h-9 w-full touch-manipulation items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors data-focus:bg-surface-raised-hover focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-volt disabled:cursor-not-allowed disabled:opacity-40 motion-reduce:transition-none ${item.className ?? 'text-muted-foreground'}`}>
              {item.icon}{item.label}
            </button>
          </MenuItem>
        ))}
        <div className="mt-1 flex items-center gap-1 border-t border-border px-2.5 pt-1">
          <span className="mr-auto text-xs text-muted-foreground">Move</span>
          {[
            { label: 'Move up', enabled: canMoveUp, onClick: onMoveUp, Icon: IconChevronUp },
            { label: 'Move down', enabled: canMoveDown, onClick: onMoveDown, Icon: IconChevronDown },
          ].map(({ label, enabled, onClick, Icon }) => (
            <MenuItem key={label} disabled={!enabled}>
              <button type="button" aria-label={label} title={label} disabled={!enabled}
                onClick={event => { event.stopPropagation(); onClick(); }}
                className="flex h-8 w-8 touch-manipulation items-center justify-center rounded-md text-muted-foreground transition-colors data-focus:bg-surface-raised-hover data-focus:text-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-volt disabled:cursor-not-allowed disabled:opacity-30 motion-reduce:transition-none">
                <Icon aria-hidden="true" className="h-4 w-4" />
              </button>
            </MenuItem>
          ))}
        </div>
      </MenuItems>
    </Menu>
  );
}
